import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { share, map } from 'rxjs/operators';

import { Course, CourseResources, Note } from './models';
import { LessonAssignment } from '../lesson/models';
import { ModuleOutlineItem } from './models/module-outline-item';
import { ModuleTestItem } from './models/module-test-item';
import { ModuleOverview } from '../module/models/module-overview';
import { ModuleItem } from './models/module-item';
import { AuthService } from '../shared/services/auth.service';
import { StudioCourseModule } from './models/course-module-assignment';

@Injectable()
export class CourseService {
  courses$: BehaviorSubject<Course[]> = new BehaviorSubject<Course[]>(null);
  private _anonymousCourses$: Course[];

  get courses() {
    return this.courses$.getValue();
  }

  constructor(
    private _http: HttpClient,
    private _authService: AuthService
  ) {
    this.init();
  }

  private init() {
    if (this._authService.isAuthenticated()) {
      this.loadCourses();
    } else {
      this.loadAnonymousCourses();
    }
  }

  getCourse(courseId: number): Course {
    if (this._authService.isAuthenticated()) {
      return this.courses$.getValue().find(t => t.Id === courseId);
    } else {
      return this._anonymousCourses$.find(t => t.Id == courseId);
    }
  }

  /** Loads the course data into the service and emits the result through courses$ */
  loadCourses(): any {
    this.fetchCourses(this._authService.userId).subscribe(courses => {
      this.courses$.next(courses);
    });
  }

  loadAnonymousCourses(): Observable<Course[]> {
    return this.fetchAnonymousCourses();
  }

  getExternalCourseUrl(productId: number): Observable<string> {
    return this.fetchCourseUrl(this._authService.userId, productId);
  }

  getInProgressLessonAssignment(courseId: number): Observable<LessonAssignment> {
    return this._http.get<LessonAssignment>('api/module/InProgressLessonAssignment/' + courseId);
  }

  getModules(courseId: number): Observable<ModuleOutlineItem[]> {
    return this._http
      .get<ModuleOutlineItem[]>('api/module/' + courseId)
      .pipe(map(response => response.map(item => new ModuleOutlineItem(item))));
  }

  getModuleSectionsLineItems(moduleId: number): Observable<ModuleItem> {
    return this._http
      .get<ModuleItem>('api/module/Sections/' + moduleId)
      .pipe(map(response => new ModuleItem(response)));
  }

  getModuleTestsLineItems(moduleId: number): Observable<ModuleTestItem[]> {
    return this._http
      .get<ModuleTestItem[]>('api/module/Tests/' + moduleId)
      .pipe(map(response => response.map(item => new ModuleTestItem(item))));
  }

  getModuleInfo(moduleId: number): Observable<ModuleOverview> {
    return this._http
      .get<ModuleOverview>('api/module/Info/' + moduleId)
      .pipe(map(response => new ModuleOverview(response)));
  }

  getCourseAssignments(courseId: number): Observable<StudioCourseModule[]> {
    return this._http
      .get<StudioCourseModule[]>('api/course/assignments/' + courseId)
      .pipe(map(response => response.map(item => new StudioCourseModule(item))));
  }

  fetchCourse(courseId: number): Observable<Course> {
    if (this._authService.isAuthenticated()) {
      return this._http
        .get('api/course/getCourse/' + courseId)
        .pipe(map(t => new Course().deserialize(t)));
    } else {
      return this._http
        .get('api/course/getAnonymousCourse/' + courseId)
        .pipe(map(t => new Course().deserialize(t)));
    }
  }

  fetchCourseProgress(courseId: number, userId: number): Observable<Course> {
    return this._http
      .get('api/course/getCourseProgress/' + courseId + '/ ' + userId)
      .pipe(map(t => new Course().deserialize(t)));
  }

  private fetchCourses(userId: number): Observable<Course[]> {
    return this._http
      .get<Course[]>('api/course/getAllCourseList/' + userId)
      .pipe(map(t => t.map(s => new Course().deserialize(s))));
  }

  private fetchAnonymousCourses(): Observable<Course[]> {
    const obs = this._http.get<Course[]>('api/course/getAllAnonymousCourseList/').pipe(
      map(t => t.map(s => new Course().deserialize(s))),
      share()
    );

    if (this._anonymousCourses$ != null) {
      return of(this._anonymousCourses$);
    } else {
      obs.subscribe(data => {
        this._anonymousCourses$ = data;
      });
      return obs;
    }
  }

  private fetchCourseUrl(userId: number, productId: number): Observable<string> {
    return this._http
      .get(`api/course/getExternalProductUrl/${userId}/${productId}`)
      .pipe(map(response => response as string));
  }

  fetchCourseResources(courseId: number): Observable<CourseResources> {
    return this._http
      .get('api/course/getCourseResources/' + courseId)
      .pipe(map(response => new CourseResources(response)));
  }

  userHasLicenseForCourse(courseId: number, userId: number): Observable<boolean> {
    return this._http
      .get('api/course/userhaslicenseforcourse/' + courseId + '/ ' + userId)
      .pipe(map(response => response as boolean));
  }

  isCourseComplete(courseId: number, userId: number): Observable<boolean> {
    return this._http
      .get('api/course/iscoursecomplete/' + courseId + '/ ' + userId)
      .pipe(map(response => response as boolean));
  }

  getNote(courseId: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http
      .get('api/course/note/' + courseId, httpOptions)
      .pipe(map(response => response));
  }

  saveNote(n: Note): Observable<number> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http
      .post('api/course/note', JSON.stringify(n), httpOptions)
      .pipe(map(response => response as number));
  }

  initializeCourse(courseId: number) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http
      .post('api/course/initialize/' + courseId, {}, httpOptions)
      .pipe(map(response => response));
  }
}
